<nav class="nav border-b border-gray-200 bg-white/95 backdrop-blur-md fixed top-0 left-0 right-0 w-full z-40 overflow-visible">
    <div
        class="nav-toolbar mx-auto flex w-full max-w-7xl min-h-12 flex-nowrap items-center gap-x-2 gap-y-0 px-4 py-2 sm:px-6 md:gap-3 md:py-1.5"
    >
        <button
            type="button"
            (click)="toggleMobileMenu()"
            class="nav-menu-trigger flex shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white p-2 text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 md:hidden"
            [attr.aria-expanded]="showMobileMenu()"
            aria-controls="nav-main-drawer"
            aria-label="Open navigation menu"
        >
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                @if (!showMobileMenu()) {
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
                } @else {
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                }
            </svg>
        </button>

        <!-- Desktop: single row + horizontal scroll; flyout menu is fixed (see below) so it is not clipped -->
        <div
            class="nav-desktop-strip hidden min-h-0 min-w-0 flex-1 md:flex md:flex-nowrap md:items-center md:justify-start md:gap-1 md:overflow-x-auto md:overflow-y-visible md:py-0.5 lg:gap-2 [scrollbar-width:thin]"
            (scroll)="syncDropdownPanelPosition()"
        >
            @for (item of navItems; track item.label) {
                @if (item.canAccess && !item.isDropdown) {
                    <a
                        [routerLink]="item.path"
                        [class.active]="isActive(item.path!)"
                        class="nav-button shrink-0"
                    >
                        {{ item.label }}
                    </a>
                }
                @if (item.canAccess && item.isDropdown) {
                    <div class="nav-dropdown-root relative shrink-0">
                        <button
                            type="button"
                            [attr.data-nav-dropdown]="item.label"
                            (click)="toggleDropdown($event, item.label)"
                            [class.active]="activeDropdown === item.label"
                            class="nav-button flex max-w-[11rem] items-center gap-1 truncate sm:max-w-none"
                            [attr.aria-expanded]="activeDropdown === item.label"
                            aria-haspopup="true"
                        >
                            <span class="truncate">{{ item.label }}</span>
                            <svg
                                class="h-3 w-3 shrink-0 transition-transform duration-200"
                                [class.rotate-180]="activeDropdown === item.label"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                            </svg>
                        </button>
                    </div>
                }
            }
        </div>

        <div class="relative ml-auto flex shrink-0 items-center md:ml-0">
            <button
                type="button"
                (click)="toggleUserDropdown()"
                class="nav-user-trigger flex rounded-full border border-gray-200 bg-white p-0 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                aria-label="User menu"
                aria-haspopup="true"
                [attr.aria-expanded]="showUserDropdown()"
            >
                <img
                    class="h-6 w-6 rounded-full bg-gray-100"
                    ngSrc="./assets/icons/profile.svg"
                    alt=""
                    height="24"
                    width="24"
                />
            </button>

            @if (showUserDropdown()) {
                <div
                    class="absolute right-0 top-full z-[60] mt-2 w-52 rounded-lg border border-gray-200 bg-white"
                    id="user-dropdown"
                    role="menu"
                >
                    <ul class="py-1">
                        <li>
                            <span class="block border-b border-gray-100 px-4 py-2 text-sm text-gray-700">{{ userName() }}</span>
                        </li>
                        <li>
                            <button
                                type="button"
                                (click)="logOut()"
                                class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                            >
                                Sign out
                            </button>
                        </li>
                    </ul>
                </div>
            }
        </div>
    </div>

    @if (dropdownMenu(); as dItem) {
        <div
            class="nav-desktop-dropdown-panel fixed z-[100] max-h-[min(70vh,24rem)] overflow-y-auto overscroll-contain rounded-md border border-gray-200 bg-white py-1"
            [style.top.px]="dropdownPanelPosition()?.top ?? 0"
            [style.left.px]="dropdownPanelPosition()?.left ?? 0"
            [style.minWidth.px]="dropdownPanelPosition()?.minWidth ?? 0"
            role="menu"
        >
            @for (subItem of dItem.children!; track subItem.label) {
                @if (subItem.canAccess) {
                    <a
                        [routerLink]="subItem.path"
                        (click)="onDesktopNavLinkClick()"
                        class="dropdown-item block whitespace-nowrap px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                        {{ subItem.label }}
                    </a>
                }
            }
        </div>
    }

    <!-- Mobile only: slide-out drawer -->
    @if (showMobileMenu()) {
        <div
            id="nav-main-drawer"
            class="nav-drawer-root fixed inset-0 z-[100] flex md:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Application navigation"
        >
            <button
                type="button"
                class="absolute inset-0 bg-black/40"
                aria-label="Close navigation"
                (click)="closeMobileMenu()"
            ></button>
            <aside
                class="nav-drawer-panel relative z-[101] flex h-full w-[min(22rem,92vw)] max-w-full flex-col border-r border-gray-200 bg-white"
                (click)="$event.stopPropagation()"
            >
                <div class="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                    <span class="text-sm font-semibold text-gray-900">Menu</span>
                    <button
                        type="button"
                        class="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                        aria-label="Close navigation"
                        (click)="closeMobileMenu()"
                    >
                        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                </div>

                <div class="nav-drawer-scroll-target min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-2">
                    @for (item of navItems; track item.label) {
                        @if (item.canAccess && !item.isDropdown) {
                            <a
                                [routerLink]="item.path"
                                [class.active]="isActive(item.path!)"
                                (click)="closeMobileMenu()"
                                class="mobile-nav-item mb-1 block rounded-md px-3 py-2.5 text-sm font-medium text-gray-800 hover:bg-gray-50"
                            >
                                {{ item.label }}
                            </a>
                        }
                        @if (item.canAccess && item.isDropdown) {
                            <div class="mb-1 border-b border-gray-50 pb-1 last:border-b-0">
                                <button
                                    type="button"
                                    (click)="toggleMobileDropdown(item.label)"
                                    [class.active]="activeMobileDropdown === item.label"
                                    class="mobile-nav-item flex w-full items-center justify-between rounded-md px-3 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-gray-50"
                                >
                                    {{ item.label }}
                                    <svg
                                        class="h-4 w-4 shrink-0 transition-transform duration-200"
                                        [class.rotate-180]="activeMobileDropdown === item.label"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                                    </svg>
                                </button>
                                @if (activeMobileDropdown === item.label) {
                                    <div class="ml-1 mt-1 space-y-0.5 border-l-2 border-gray-200 pl-3">
                                        @for (subItem of item.children; track subItem.label) {
                                            @if (subItem.canAccess) {
                                                <a
                                                    [routerLink]="subItem.path"
                                                    (click)="closeMobileMenu()"
                                                    class="block rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                                                >
                                                    {{ subItem.label }}
                                                </a>
                                            }
                                        }
                                    </div>
                                }
                            </div>
                        }
                    }
                </div>

                <div class="shrink-0 border-t border-gray-200 bg-gray-50 px-3 py-3">
                    <button
                        type="button"
                        (click)="logOut(); closeMobileMenu()"
                        class="w-full rounded-md border border-gray-200 bg-white px-3 py-2.5 text-left text-sm font-medium text-gray-900 hover:bg-gray-100"
                    >
                        Sign out
                    </button>
                </div>
            </aside>
        </div>
    }

    <app-breadcrumb></app-breadcrumb>
</nav>
<app-network-status-banner></app-network-status-banner>
